<template>
    <form
        method="GET"
        :action="formAction"
        @submit="submitCheckout"
        id="checkout-form"
    >
        <template v-if="!menuPrecedesQty">
            <template v-if="product.isEvent">
                <label for="qty" class="block text-sm font-medium text-black"
                    >Approximately how many participants?</label
                >
                <select
                    v-model="qty"
                    name="qty"
                    id="qty"
                    class="mt-2 block w-full rounded border-gray-300 py-3 pl-3 pr-10 text-base at480:text-sm"
                >
                    <option v-for="count in participantRange" :value="count">
                        {{ count }}
                        {{
                            count === qty
                                ? count === 1
                                    ? 'participant'
                                    : 'participants'
                                : ''
                        }}
                    </option>
                </select>
            </template>
            <template v-else>
                <label for="qty" class="sr-only">Quantity</label>
                <input
                    v-model="qty"
                    name="qty"
                    id="qty"
                    type="number"
                    class="w-full rounded border-gray-300 px-3 py-2.5 placeholder:text-sm"
                    :min="minQty"
                    :max="maxQty"
                    :placeholder="inputPlaceholder"
                    required
                    aria-required="true"
            /></template>
        </template>

        <!-- Variant menu -->
        <Listbox
            v-for="(variantProperty, i1) in variantProperties"
            :key="variantProperty.label"
            v-model="variantSelections[i1]"
            as="div"
            class="relative mt-4 text-black"
            :data-testid="'variant-menu-' + i1"
        >
            <ListboxButton
                class="listbox-button flex w-full items-center justify-between gap-x-1.5 rounded border border-gray-300 bg-white p-3 text-sm"
                :class="{
                    'cursor-not-allowed text-gray-500':
                        i1 > 0 && !variantSelections[i1 - 1].value,
                    'ring-2 ring-red-500':
                        validating && !variantSelections[i1].value,
                }"
                :disabled="i1 > 0 && !variantSelections[i1 - 1].value"
            >
                <span class="flex items-center">
                    <swatch
                        v-if="variantSelections[i1].swatch"
                        :swatch="variantSelections[i1].swatch"
                        :alt="variantSelections[i1].value"
                    ></swatch>
                    <span class="block truncate">
                        {{
                            variantSelections[i1].value ||
                            `Choose your ${variantProperty.label}`
                        }}</span
                    >
                </span>
                <ChevronDownIcon
                    class="h-5 w-5 text-gray-500"
                    aria-hidden="true"
                >
                </ChevronDownIcon>
            </ListboxButton>
            <transition
                leave-active-class="transition ease-in duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <ListboxOptions
                    class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-gray-300 focus:outline-none"
                >
                    <ListboxOption
                        as="template"
                        v-for="(variant, i2) in optionsForSelection(
                            i1 > 0 ? variantSelections[i1 - 1] : null
                        )"
                        :index="i2"
                        :key="variant.slug"
                        :value="variant"
                        v-slot="{ active, selected }"
                        :data-testid="'variant-menu-' + i1 + '-option-' + i2"
                    >
                        <li
                            class="listbox-option relative flex cursor-pointer select-none items-center p-3 ui-selected:bg-gray-200 ui-active:bg-gray-200"
                        >
                            <swatch
                                v-if="variant.swatch"
                                :swatch="variant.swatch"
                                :alt="variant.value"
                            ></swatch>
                            <span class="block truncate">{{
                                variant.value
                            }}</span>
                        </li>
                    </ListboxOption>
                </ListboxOptions>
            </transition>
        </Listbox>

        <div v-if="menuPrecedesQty" class="mt-4">
            <template v-if="product.isEvent">
                <label for="qty" class="block text-sm font-medium text-black"
                    >Approximately how many participants?</label
                >
                <select
                    v-model="qty"
                    name="qty"
                    id="qty"
                    class="mt-2 block w-full rounded border-gray-300 py-3 pl-3 pr-10 text-base at480:text-sm"
                >
                    <option v-for="count in participantRange" :value="count">
                        {{ count }}
                        {{
                            count === qty
                                ? count === 1
                                    ? 'participant'
                                    : 'participants'
                                : ''
                        }}
                    </option>
                </select>
            </template>
            <template v-else>
                <label for="qty" class="sr-only">Quantity</label>
                <input
                    v-model="qty"
                    name="qty"
                    id="qty"
                    type="number"
                    class="w-full rounded border-gray-300 px-3 py-2.5 placeholder:text-sm"
                    :min="minQty"
                    :max="maxQty"
                    :placeholder="inputPlaceholder"
                    required
                    aria-required="true"
                />
            </template>
        </div>
        <input
            data-testid="addon-config-encoded-value"
            type="hidden"
            name="ao"
            :value="encodedCheckoutParameter"
        />
        <button
            type="submit"
            class="btn-orange button-custom-branded mt-4 w-full"
            data-testid="checkout-button"
        >
            {{ checkoutButtonText }}
        </button>
    </form>
</template>

<script setup>
import { useListingCart } from '@/composables/useListingCart';
import { useStorefront } from '@/composables/useStorefront';
import { trackAddToCartEvent } from '@/helpers/gtm';
import { ServiceRouter } from '@/service-router';
import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/solid';
import range from 'lodash/range';
import {
    computed,
    inject,
    onMounted,
    ref,
    shallowReactive,
    toRaw,
    toValue,
    watch,
} from 'vue';
import Swatch from './VariantMenu/Swatch.vue';

const emit = defineEmits(['variant-selected', 'qty-changed']);
const product = inject('product');
const { getStorefront } = useStorefront();
const storefront = getStorefront();
const serviceRouter = new ServiceRouter();

const { encodedCheckoutParameter } = useListingCart(product.uuid);

const variantProperties = product.variants?.properties;
const variantSelections = shallowReactive([]);
for (const property of variantProperties) {
    variantSelections.push({});
}
const requireVariant = product.variants?.variants.length > 0;
const menuPrecedesQty =
    requireVariant && variantProperties.filter((p) => p.precedesQty).length > 0; // render the variant menu before the qty dropdown.
const selectedVariant = computed(
    () => variantSelections[variantSelections.length - 1]
);
const validating = ref(false);
const qty = ref(product.isEvent ? product.participants.min : '');

const minQty = computed(() => {
    const selected = toValue(selectedVariant);
    return product.isEvent
        ? (((selected?.product ?? false)
              ? selected.product.participants.min
              : product.participants.min) ?? 1)
        : (((selected?.product ?? false)
              ? selected.product.shipping?.minQty
              : product.shipping?.minQty) ?? 1);
});
const maxQty = computed(() => {
    const selected = toValue(selectedVariant);
    return product.isEvent
        ? (((selected?.product ?? false)
              ? selected.product.participants.max
              : product.participants.max) ?? 1000)
        : (((selected?.product ?? false)
              ? selected.product.shipping?.maxQty
              : product.shipping?.maxQty) ?? 5000);
});
const participantRange = computed(() => range(minQty.value, maxQty.value + 1));
const formAction = computed(() => {
    if (requireVariant && toValue(selectedVariant)?.slug) {
        return serviceRouter.storefrontCheckoutUrl(
            storefront.subdomain,
            'book/' + toValue(selectedVariant).slug
        );
    } else if (!requireVariant) {
        return serviceRouter.storefrontCheckoutUrl(
            storefront.subdomain,
            'book/' + product.slug
        );
    }
});

const inputPlaceholder =
    'Quantity' + (minQty.value > 1 ? ` - minimum ${minQty.value} items` : '');

const checkoutButtonText = product.isProduct
    ? 'Buy now'
    : product.price.isFree && product.brickVirtual?.isSelfServiceSubscription
      ? 'Start or schedule'
      : storefront.isPublic
        ? 'Make a reservation'
        : 'Submit an event request';

onMounted(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlQty = parseInt(searchParams.get('qty') ?? 0);
    if (urlQty >= minQty.value && urlQty <= maxQty.value) {
        qty.value = urlQty;
        emit('qty-changed', urlQty);
    }
    if (requireVariant) {
        for (let i = 0; i < variantProperties.length; i++) {
            const property = variantProperties[i];
            const value = searchParams.get(property.label);
            if (value) {
                const options = optionsForSelection(
                    i > 0 ? variantSelections[i - 1] : null
                );
                for (const option of options) {
                    if (option.value === value) {
                        variantSelections[i] = option;
                        break;
                    }
                }
            }
        }

        // register the selected variant from the URL
        if (Object.keys(selectedVariant.value).length > 0) {
            emit('variant-selected', selectedVariant.value);
        }

        watch(
            () => [...variantSelections],
            (newVal, oldVal) => {
                // unset all subsequent selections when a dropdown changes
                for (let i = 0; i < newVal.length; i++) {
                    if (newVal[i] !== oldVal[i]) {
                        for (let j = i + 1; j < newVal.length; j++) {
                            variantSelections[j] = {};
                        }
                        break;
                    }
                }
            }
        );

        watch(selectedVariant, () => {
            const variantValue = toValue(selectedVariant);
            emit('variant-selected', variantValue);
            const searchParams = new URLSearchParams();
            if (qty.value) {
                if (qty.value < minQty.value) {
                    qty.value = minQty.value;
                } else if (qty.value > maxQty.value) {
                    qty.value = maxQty.value;
                }
                searchParams.set('qty', qty.value);
            }
            // update product with properties from the selected variant
            for (const property in variantValue.product) {
                product[property] = variantValue.product[property];
            }

            for (const selection of variantSelections) {
                if (selection.property ?? false) {
                    searchParams.set(selection.property.label, selection.value);
                }
            }
            replaceSearchParams(searchParams);
        });
    }
});

watch(qty, (newVal) => {
    emit('qty-changed', newVal);
    const searchParams = new URLSearchParams(window.location.search);
    if (newVal >= minQty.value && newVal <= maxQty.value) {
        searchParams.set('qty', newVal);
    } else {
        searchParams.delete('qty');
    }
    replaceSearchParams(searchParams);
});

function replaceSearchParams(searchParams) {
    const url =
        window.location.origin +
        window.location.pathname +
        '?' +
        searchParams.toString();
    window.history.replaceState({ path: url }, '', url);
}

function optionsForSelection(variant) {
    // assume top level if variant is null
    return toRaw(variant?.children ?? product.variants.variants);
}

function validate() {
    validating.value = true;
    return !!selectedVariant.value.value;
}

function submitCheckout(e) {
    const form = e.target;
    if (
        form.checkValidity() &&
        (!requireVariant || (validate() && toValue(selectedVariant)?.slug))
    ) {
        trackAddToCartEvent(product);
        return true;
    }
    e.preventDefault();
    return false;
}
</script>
